/**
 * Shared validation for human-readable resource names (projects, agents, tools, etc.).
 *
 * Allowed: letters, digits, spaces, hyphens, underscores.
 *
 * Agent and workflow names use ONE platform-wide rule — `validateAgentWorkflowName`
 * below — shared by Agent Studio, Agent Workflows, and the Agent Registry, and
 * mirrored on the backend (`app/core/validators.py` in the main API,
 * `app/core/workflow_name.py` in AIWorkflows).
 */

import { hasAsciiLetterOrDigit, isResourceNameChars } from './safeRegex'

/** Letters, digits, spaces, hyphens, underscores (literal for docs/tests only). */
export const RESOURCE_NAME_PATTERN = /^[a-zA-Z0-9\s\-_]+$/

export const RESOURCE_NAME_MIN_LENGTH = 3

/** Default max length aligned with common backend display-name limits */
export const RESOURCE_NAME_DEFAULT_MAX_LENGTH = 96

/**
 * Canonical agent/workflow display-name rule (Agent Studio, Agent Workflows, Registry).
 * Min/max must match `AGENT_WORKFLOW_NAME_MIN/MAX_LENGTH` on both backends.
 */
export const AGENT_WORKFLOW_NAME_MIN_LENGTH = 3
export const AGENT_WORKFLOW_NAME_MAX_LENGTH = 64

/** Allowed characters shown in validation copy (kept identical to backend messages). */
export const AGENT_WORKFLOW_NAME_ALLOWED_CHARS_MESSAGE =
  'letters, numbers, spaces, hyphens, underscores, brackets, colons, and ampersands'

/** Options every agent/workflow name input must pass to stay aligned with the backend. */
export const AGENT_WORKFLOW_NAME_OPTIONS = {
  minLength: AGENT_WORKFLOW_NAME_MIN_LENGTH,
  maxLength: AGENT_WORKFLOW_NAME_MAX_LENGTH,
  preventStartingWithNumber: true,
  allowAmpersand: true,
} as const

export interface ValidateResourceNameOptions {
  fieldName?: string
  minLength?: number
  maxLength?: number
  /** When true, empty/whitespace-only input is valid (optional fields) */
  allowEmpty?: boolean
  /** When true, input cannot start with a number */
  preventStartingWithNumber?: boolean
  /** When true, dots are permitted in the resource name */
  allowDots?: boolean
  /** When true, brackets/parentheses are permitted in the resource name */
  allowBrackets?: boolean
  /** When true, colons are permitted in the resource name */
  allowColons?: boolean
  /** When true, ampersands are permitted in the resource name */
  allowAmpersand?: boolean
}

function buildInvalidCharsMessage(fieldName: string, extras: string[]): string {
  if (extras.length === 0) {
    return `${fieldName} can only contain letters, numbers, spaces, hyphens, and underscores`
  }
  if (extras.length === 1) {
    return `${fieldName} can only contain letters, numbers, spaces, hyphens, underscores, and ${extras[0]}`
  }
  if (extras.length === 2) {
    return `${fieldName} can only contain letters, numbers, spaces, hyphens, underscores, ${extras[0]}, and ${extras[1]}`
  }
  return `${fieldName} can only contain letters, numbers, spaces, hyphens, underscores, ${extras.slice(0, -1).join(', ')}, and ${extras[extras.length - 1]}`
}

/**
 * Returns an error message when invalid, or null when valid.
 */
export function validateResourceName(
  value: string,
  options: ValidateResourceNameOptions = {}
): string | null {
  const {
    fieldName = 'Name',
    minLength = RESOURCE_NAME_MIN_LENGTH,
    maxLength = RESOURCE_NAME_DEFAULT_MAX_LENGTH,
    allowEmpty = false,
    preventStartingWithNumber = false,
    allowDots = false,
    allowBrackets = true,
    allowColons = true,
    allowAmpersand = false,
  } = options

  const trimmed = value.trim()

  if (!trimmed) {
    return allowEmpty ? null : `${fieldName} is required`
  }

  if (value.length > maxLength) {
    return `${fieldName} must be ${maxLength} characters or fewer`
  }

  if (trimmed.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`
  }

  if (!isResourceNameChars(value, allowDots, allowBrackets, allowColons, allowAmpersand)) {
    const extras: string[] = []
    if (allowDots) extras.push('dots')
    if (allowBrackets) extras.push('brackets')
    if (allowColons) extras.push('colons')
    if (allowAmpersand) extras.push('ampersands')
    return buildInvalidCharsMessage(fieldName, extras)
  }

  if (preventStartingWithNumber && /^\d/.test(trimmed)) {
    return `${fieldName} cannot start with a number`
  }

  return null
}

export function isValidResourceName(
  value: string,
  options?: ValidateResourceNameOptions
): boolean {
  return validateResourceName(value, options) === null
}

/** Strip characters that are not allowed in resource display names. */
export function filterResourceNameChars(
  value: string,
  allowDots: boolean = false,
  allowBrackets: boolean = true,
  allowColons: boolean = true,
  allowAmpersand: boolean = false
): string {
  let result = ''
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i)
    const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
    const isDigit = c >= 48 && c <= 57
    const isSpace = c === 32
    const isHyphen = c === 45
    const isUnderscore = c === 95
    const isDot = c === 46
    const isBracket = c === 40 || c === 41 || c === 91 || c === 93 || c === 123 || c === 125 // (), [], {}
    const isColon = c === 58 // :
    const isAmpersand = c === 38 // &
    
    if (
      isLetter ||
      isDigit ||
      isSpace ||
      isHyphen ||
      isUnderscore ||
      (allowDots && isDot) ||
      (allowBrackets && isBracket) ||
      (allowColons && isColon) ||
      (allowAmpersand && isAmpersand)
    ) {
      result += value[i]
    }
  }
  return result
}

/**
 * Format/length validation for in-progress input (skips required check while empty).
 */
export function validateResourceNameInput(
  value: string,
  options: ValidateResourceNameOptions = {}
): string | null {
  if (!value.trim()) {
    return null
  }
  return validateResourceName(value, options)
}

/**
 * Canonical agent/workflow name validation — the ONE rule used by Agent Studio
 * create/rename, Workflows create/edit/rename, and Agent Registry registration.
 *
 * Returns `null` when valid, else a user-facing error string. Pass
 * `required: false` for in-progress input where empty means "not filled yet".
 */
export function validateAgentWorkflowName(
  value: string,
  options: { fieldName?: string; required?: boolean } = {}
): string | null {
  const { fieldName = 'Name', required = true } = options

  if (!value.trim() && !required) return null

  const baseError = validateResourceName(value, {
    fieldName,
    ...AGENT_WORKFLOW_NAME_OPTIONS,
  })
  if (baseError) return baseError

  // Backend rejects names made up only of separators ("---", "___"); match it so
  // the UI never submits something the API answers with a 422.
  if (!hasAsciiLetterOrDigit(value.trim())) {
    return `${fieldName} must contain at least one letter or number`
  }

  return null
}

/** Boolean form of `validateAgentWorkflowName` for enabling submit buttons. */
export function isValidAgentWorkflowName(value: string, fieldName = 'Name'): boolean {
  return validateAgentWorkflowName(value, { fieldName }) === null
}

